// telemetry-gate.js
// Decides whether browser telemetry (Firebase Analytics + Performance
// Monitoring) may run at all. shouldEnableTelemetry is pure. The one impure
// function, readTelemetryOverride, lives here so the whole decision is in one
// file; it is the only storage read the gate makes.
//
// The gate is a RUNTIME hostname check, not a build-time env flag, because the
// Playwright performance harness serves the real production build from
// localhost. `import.meta.env.PROD` is true inside that harness, so a build
// flag would silently instrument the very runs whose frame times we measure.

/**
 * Hosts that serve production traffic. Telemetry runs only here. Exact match,
 * so Firebase Hosting preview channels (binder-browser--<name>.web.app) never
 * qualify: channel testing must not land in production percentiles.
 */
export const TELEMETRY_HOSTS = Object.freeze([
  'binderbrowser.com',
  'www.binderbrowser.com',
  'binderbrowser.app',
  'www.binderbrowser.app',
  'binder-browser.web.app',
  'binder-browser.firebaseapp.com',
]);

/** localStorage key holding the manual override. */
export const TELEMETRY_OVERRIDE_KEY = 'bb_telemetry';

/**
 * Should telemetry be installed for this page view?
 *
 * Precedence, highest first: an explicit override ('off' then 'on'), then
 * automation, then the host allowlist. Set the override to 'on' to measure a
 * preview channel or localhost.
 *
 * @param {object} input
 * @param {string} input.hostname          location.hostname
 * @param {boolean} [input.webdriver]      navigator.webdriver (Playwright sets true)
 * @param {string | null} [input.override] 'on' | 'off' | anything else (ignored)
 * @returns {boolean}
 */
export function shouldEnableTelemetry({ hostname, webdriver = false, override = null }) {
  if (override === 'off') return false;
  if (override === 'on') return true;
  if (webdriver) return false;
  return TELEMETRY_HOSTS.includes(String(hostname ?? ''));
}

/**
 * Read the manual override from localStorage. Storage access throws in Safari
 * private mode and is absent under SSR, so every failure means "no override".
 * @returns {string | null}
 */
export function readTelemetryOverride() {
  try {
    const value = globalThis.localStorage?.getItem(TELEMETRY_OVERRIDE_KEY);
    return value === null || value === undefined ? null : String(value);
  } catch {
    return null;
  }
}
